import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { createComment } from 'api/comments/createComment';
import { updateComment } from 'api/comments/updateComment';
import { setDwpCaseId } from 'api/cantrack/getDashboardData';
import { dashboardKeys } from './dashboardQueryKeys';
import type { DailyWorkPlanState, DashboardData } from 'containers/pages/cantrackDashboard/types';
import type { CANtrackComment } from 'containers/pages/comments/types/comment';

function useDailyWorkPlan(dailyWorkPlan: DailyWorkPlanState | null) {
  const queryClient = useQueryClient();
  const { userInfo } = useSelector(selectUserInfo) as any;
  const { notifySuccess, notifyError } = useNotificationProvider();
  const [loading, setLoading] = useState(false);

  const userId = userInfo?.userId ?? '';
  const contactId = userInfo?.contactId ?? '';

  const hasDailyPlanCase = !!dailyWorkPlan;
  const dailyPlanCaseId = dailyWorkPlan?.caseId ?? null;
  const todaysPlan = dailyWorkPlan?.todayComment ?? null;
  const planSubmittedToday = dailyWorkPlan?.submittedToday ?? false;

  const queryKey = dashboardKeys.data(userId);

  const configureDwpCase = useCallback(
    (caseId: number) => {
      setDwpCaseId(caseId);
      queryClient.invalidateQueries({ queryKey });
    },
    [queryClient, queryKey],
  );

  const submitPlan = useCallback(
    async (summary: string, description: string) => {
      if (!dailyPlanCaseId) {
        notifyError('No Daily Work Plan case configured.');
        return;
      }
      setLoading(true);

      const comment: CANtrackComment = {
        id: 0,
        title: summary,
        description,
        date: new Date(),
        timeRegular: 0.1,
        timeOver: 0,
        caseID: dailyPlanCaseId,
        caseTitle: dailyWorkPlan?.caseTitle ?? '',
        customerID: 0,
        customerName: '',
        gpda: {
          group: { id: 0, title: '' },
          project: { id: 0, title: '' },
          deliverable: { id: 0, title: '' },
          activity: { id: 0, title: '' },
        },
        billable: false,
        userId,
        contactId,
      };

      // Optimistically update the cache
      const previous = queryClient.getQueryData<DashboardData>(queryKey);
      if (previous) {
        queryClient.setQueryData<DashboardData>(queryKey, {
          ...previous,
          dailyWorkPlan: {
            caseId: dailyPlanCaseId,
            caseTitle: dailyWorkPlan?.caseTitle ?? '',
            todayComment: comment,
            submittedToday: true,
          },
        });
      }

      try {
        await createComment(comment);
        notifySuccess('Daily Work Plan submitted.');
        // Refetch to get the real server data (e.g. actual comment ID)
        queryClient.invalidateQueries({ queryKey });
      } catch (error) {
        console.error('[useDailyWorkPlan] Submit failed:', error);
        notifyError('Failed to submit Daily Work Plan.');
        // Roll back optimistic update
        if (previous) {
          queryClient.setQueryData<DashboardData>(queryKey, previous);
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [dailyPlanCaseId, dailyWorkPlan?.caseTitle, contactId, userId, queryClient, queryKey, notifySuccess, notifyError],
  );

  const updatePlan = useCallback(
    async (summary: string, description: string) => {
      if (!todaysPlan) {
        console.error('[useDailyWorkPlan] No existing plan to update');
        return;
      }
      setLoading(true);

      const updated: CANtrackComment = {
        ...todaysPlan,
        title: summary,
        description,
      };

      // Optimistically update the cache
      const previous = queryClient.getQueryData<DashboardData>(queryKey);
      if (previous && previous.dailyWorkPlan) {
        queryClient.setQueryData<DashboardData>(queryKey, {
          ...previous,
          dailyWorkPlan: {
            ...previous.dailyWorkPlan,
            todayComment: updated,
          },
        });
      }

      try {
        await updateComment(updated);
        notifySuccess('Daily Work Plan updated.');
        queryClient.invalidateQueries({ queryKey });
      } catch (error) {
        console.error('[useDailyWorkPlan] Update failed:', error);
        notifyError('Failed to update Daily Work Plan.');
        // Roll back optimistic update
        if (previous) {
          queryClient.setQueryData<DashboardData>(queryKey, previous);
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [todaysPlan, queryClient, queryKey, notifySuccess, notifyError],
  );

  return {
    state: {
      hasDailyPlanCase,
      dailyPlanCaseId,
      todaysPlan,
      planSubmittedToday,
      loading,
    },
    handler: {
      submitPlan,
      updatePlan,
      configureDwpCase,
    },
  };
}

export default useDailyWorkPlan;
